import { FC, useEffect, useState } from "react";
import { VoidFunc } from "../../../../helpers/functions/types";
import {
  ATTRIBUTE_GROUP_STYLES,
  SM_LONG_INPUT_STYLES,
} from "../../detailedPages/styles";
import { LabelAtom } from "./LabelAtom";
import { RadioGroupStyleVariant } from "../NamedRadio";

type RadioOptions = {
  value: string;
  title?: string;
  disabled?: boolean;
  icon?: JSX.Element;
}[];

const BUTTONS_LABEL_STYLES =
  "has-disabled:opacity-40 has-checked:bg-green-500 dark:has-checked:dark:bg-green-500 bg-stone-200 dark:bg-stone-700 disabled:opacity-40 align-center flex w-max items-center justify-center gap-1 rounded-md px-4 py-2 text-sm font-normal text-stone-900 shadow-sm focus:outline-none focus:ring-1 focus:ring-transparent focus-visible:ring-stone-500 dark:text-white";

const RADIO_WRAPPER_STYLES: Record<RadioGroupStyleVariant, string> = {
  buttons: `min-w-0 justify-end ${ATTRIBUTE_GROUP_STYLES} ${SM_LONG_INPUT_STYLES}`,
  wideButtons: `min-w-0 justify-end ${ATTRIBUTE_GROUP_STYLES} sm:flex-1`,
  icons: "ml-1 flex gap-x-1 w-fit",
};

const RADIO_LABEL_STYLES: Record<RadioGroupStyleVariant, string> = {
  buttons: BUTTONS_LABEL_STYLES,
  wideButtons: BUTTONS_LABEL_STYLES,
  icons: "",
};

export const RadioAtom: FC<{
  options: RadioOptions;
  initValue?: string;
  disabled?: boolean;
  onChange?: VoidFunc<string>;
  requiredMessage?: string;
  disabledMessage?: string;
  customId?: string;
  radioVariant: RadioGroupStyleVariant;
}> = ({
  initValue,
  options,
  onChange,
  requiredMessage,
  disabledMessage,
  customId,
  radioVariant,
}) => {
  const [selectedValue, setSelectedValue] = useState(initValue);

  useEffect(() => {
    setSelectedValue(initValue);
  }, [initValue]);

  const handleChange = (value: string) => {
    setSelectedValue(value);
    onChange?.(value);
  };

  return (
    <fieldset className={RADIO_WRAPPER_STYLES[radioVariant]}>
      {options.map(({ value, title, disabled, icon }) => (
        <LabelAtom
          title={title ?? value}
          htmlFor={value}
          tooltip={disabled ? disabledMessage : undefined}
          key={value}
          className={RADIO_LABEL_STYLES[radioVariant]}
          srOnly={radioVariant === "icons" && !!icon}
        >
          {icon || null}
          <input
            className="absolute opacity-0"
            type="radio"
            name={customId}
            value={value}
            checked={value === selectedValue}
            required
            data-msg-required={requiredMessage}
            id={value}
            onChange={() => handleChange(value)}
            disabled={disabled}
          />
        </LabelAtom>
      ))}
    </fieldset>
  );
};
